/**
 * Warms a page's code chunk before the user lands on it (medopd-docs 105).
 *
 * Every route in App.tsx is a `React.lazy` chunk, so the first visit to a page pays for downloading (and, on the
 * dev server, transforming) its module graph — half a second to two seconds — before anything can render. The
 * sidebar calls `preloadRoute` when the pointer enters or focus reaches an item, which is typically a few hundred
 * milliseconds before the click, so by the time the click lands the chunk is usually already in the module cache.
 *
 * The loaders repeat the same `import()` specifiers App.tsx uses: Vite resolves identical specifiers to the same
 * chunk, so nothing is bundled twice. `routePreload.test.ts` checks that every sidebar path has a loader here.
 */
export type RouteLoader = () => Promise<unknown>;

export const ROUTE_LOADERS: Record<string, RouteLoader> = {
  '/admin': () => import('../components/admin/SuperAdminDashboard'),
  '/admin/addon-requests': () => import('../pages/admin/AddonRequestsPage'),
  '/admin/ai-safety-review': () => import('../components/admin/ai-safety/AdminAiSafetyReviewPage'),
  '/admin/ai-settings': () => import('../pages/admin/ClinicAiSettingsPage'),
  '/admin/article-review': () => import('../pages/admin/ArticleReviewPage'),
  '/admin/audit': () => import('../components/admin/AuditLogs'),
  '/admin/checkin-settings': () => import('../pages/admin/ClinicCheckinSettingsPage'),
  '/admin/clinic-access': () => import('../pages/admin/ClinicAccessPage'),
  '/admin/clinic-account-requests': () => import('../pages/admin/ClinicAccountRequestsPage'),
  '/admin/clinic-profile': () => import('../pages/admin/ClinicProfilePage'),
  '/admin/consent-settings': () => import('../pages/admin/ConsentSettingsPage'),
  '/admin/app-versions': () => import('../pages/admin/AppVersionsPage'),
  '/admin/corporate': () => import('../pages/admin/CorporateProgramsPage'),
  '/admin/doctor-verifications': () => import('../pages/admin/publicProfile/DoctorVerificationQueuePage'),
  '/admin/engagement': () => import('../pages/admin/ClinicEngagementSettingsPage'),
  '/admin/erasure-requests': () => import('../pages/admin/ErasureRequestsPage'),
  '/admin/error-logs': () => import('../components/admin/SystemErrorLogs'),
  '/admin/fee-schedule': () => import('../pages/admin/FeeScheduleScreen'),
  '/admin/feedback-nps': () => import('../pages/admin/FeedbackAnalyticsPage'),
  '/admin/follow-ups': () => import('../pages/admin/FollowUpEnforcementPage'),
  '/admin/health-packages': () => import('../pages/admin/HealthPackagesPage'),
  '/admin/home-collection': () => import('../pages/admin/HomeCollectionSettingsPage'),
  '/admin/hospital-groups': () => import('../pages/admin/HospitalGroupsPage'),
  '/admin/insurance': () => import('../pages/admin/InsuranceSettingsPage'),
  '/admin/inventory': () => import('../components/admin/InventoryManagement'),
  '/admin/lab-catalog': () => import('../pages/admin/LabCatalogAdminScreen'),
  '/admin/lab-metrics': () => import('../components/admin/LabMetricManagement'),
  '/admin/lab-verifications': () => import('../pages/admin/publicProfile/LabVerificationQueuePage'),
  '/admin/medications': () => import('../components/admin/SuperAdminMedicationManagement'),
  '/admin/medicine-ordering': () => import('../pages/admin/MedicineOrderingSettingsPage'),
  '/admin/nav-settings': () => import('../pages/admin/ClinicNavSettingsPage'),
  '/admin/pharmacy-approvals': () => import('../components/admin/PharmacyManagement'),
  '/admin/photo-moderation': () => import('../pages/admin/publicProfile/PhotoModerationPage'),
  '/admin/public-profile': () => import('../pages/admin/publicProfile/ClinicPublicListingPage'),
  '/admin/quality-measures': () => import('../pages/admin/QualityMeasuresPage'),
  '/admin/question-moderation': () => import('../pages/admin/QuestionModerationPage'),
  '/admin/referrals': () => import('../pages/admin/ReferralsPage'),
  '/admin/revenue-intelligence': () => import('../pages/admin/RevenueIntelligencePage'),
  '/admin/review-moderation': () => import('../pages/admin/publicProfile/ReviewModerationPage'),
  '/admin/about-text-review': () => import('../pages/admin/publicProfile/AboutTextModerationPage'),
  '/admin/moderation-audit': () => import('../pages/admin/ModerationAuditPage'),
  '/admin/moderation-policy': () => import('../pages/admin/ModerationPolicyPage'),
  '/admin/moderation-reports': () => import('../pages/admin/ModerationReportsPage'),
  '/admin/site-visitors': () => import('../pages/admin/SiteVisitorsPage'),
  '/admin/staff': () => import('../components/admin/StaffManagement'),
  '/admin/settings': () => import('../pages/admin/SettingsHubPage'),
  '/admin/plan': () => import('../pages/admin/YourPlanPage'),
  '/admin/payment-gateway': () => import('../pages/admin/PaymentGatewaySettingsPage'),
  '/admin/labs': () => import('../components/admin/LabManagement'),
  '/admin/in-house-pharmacies': () => import('../components/admin/InHousePharmacyManagement'),
  '/admin/subscription-plans': () => import('../pages/admin/SubscriptionPlansPage'),
  '/admin/test-data': () => import('../pages/admin/TestDataStudioPage'),
  '/billing': () => import('../components/billing/BillingForm'),
  '/billing/cash-close': () => import('../pages/billing/CashClosePage'),
  '/billing/queue': () => import('../components/billing/BillingQueueScreen'),
  '/doctor/appointments': () => import('../components/appointments/AppointmentManagementComponent'),
  '/doctor/articles': () => import('../pages/doctorListing/MyArticlesPage'),
  '/doctor/bookings': () => import('../pages/doctorListing/MyBookingsPage'),
  '/doctor/clinic-ai-info': () => import('../pages/admin/ClinicKnowledgeBasePage'),
  '/doctor/feedback': () => import('../components/doctor/FeedbackDashboardPage'),
  '/doctor/lab-review': () => import('../pages/doctor/LabAiReviewPage'),
  '/doctor/notes': () => import('../components/doctor/ClinicalNotes'),
  '/doctor/practices': () => import('../pages/doctorListing/MyPracticesPage'),
  '/doctor/prescriptions': () => import('../components/doctor/Prescriptions'),
  '/doctor/public-profile': () => import('../components/publicProfile/DoctorPublicProfilePage'),
  '/doctor/questions': () => import('../pages/doctorListing/DoctorQuestionsPage'),
  '/doctor/revenue': () => import('../components/doctor/RevenueDashboard'),
  '/doctor/schedule-config': () => import('../components/appointments/DoctorScheduleConfigurationPage'),
  '/doctor/templates': () => import('../components/prescriptions/TemplatesManagement'),
  '/follow-ups': () => import('../pages/staff/FollowUpQueuePage'),
  '/growth': () => import('../pages/admin/GrowthDashboardPage'),
  '/growth/campaigns': () => import('../pages/admin/CampaignsPage'),
  '/inpatients': () => import('../components/inpatient/InpatientCensus'),
  '/procedures': () => import('../pages/ProcedureRoomPage'),
  '/treatment-plans': () => import('../pages/TreatmentPlansPage'),
  '/work-orders': () => import('../components/workOrders/WorkOrdersPage'),
  '/optical': () => import('../components/optical/OpticalPage'),
  '/eye-care': () => import('../components/eyeCare/EyeCareHubPage'),
  '/dental': () => import('../components/dental/DentalCarePage'),
  '/insurance': () => import('../pages/insurance/InsuranceDeskPage'),
  '/lab/home-collections': () => import('../pages/lab/HomeCollectionBoard'),
  '/lab/inventory': () => import('../components/lab/LabReagentInventory'),
  '/lab/receiving': () => import('../components/lab/LabReceivingPage'),
  '/lab/qc': () => import('../components/lab/LabQcPage'),
  '/lab/setup': () => import('../pages/labListing/LabSetupPage'),
  '/lab/tasks': () => import('../components/lab/workflow/LabWorklistPage'),
  '/medications': () => import('../components/medications/ClinicMedicationsList'),
  '/notifications': () => import('../pages/NotificationInbox'),
  '/onboarding': () => import('../components/onboarding/OnboardingPage'),
  '/patients': () => import('../components/patients/PatientSearch'),
  '/patients/register': () => import('../components/patients/PatientRegistration'),
  '/lab/front-desk': () => import('../components/lab/frontdesk/LabFrontDeskPage'),
  '/lab/bills': () => import('../components/lab/frontdesk/LabBillsPage'),
  '/lab/referrers': () => import('../components/lab/frontdesk/LabReferrersPage'),
  '/lab/result-rules': () => import('../pages/lab/LabResultRulesPage'),
  '/lab/report-setup': () => import('../components/lab/reports/LabReportSetupPage'),
  '/lab/tests': () => import('../components/lab/catalog/LabTestsPage'),
  '/lab/analytics': () => import('../components/lab/analytics/LabAnalyticsPage'),
  '/lab/price-lists': () => import('../components/lab/catalog/LabPriceListsPage'),
  '/pharmacy/inventory': () => import('../components/pharmacy/PharmacyMedicationInventory'),
  '/pharmacy/medicine-orders': () => import('../pages/pharmacy/MedicineOrdersBoard'),
  '/pharmacy/counter': () => import('../components/pharmacy/sales/CounterSalePage'),
  '/pharmacy/sales': () => import('../components/pharmacy/sales/SalesListPage'),
  '/pharmacy/controlled-register': () => import('../components/pharmacy/sales/ControlledRegisterPage'),
  '/pharmacy/purchases': () => import('../components/pharmacy/purchases/PurchasesPage'),
  '/pharmacy/suppliers': () => import('../components/pharmacy/purchases/SuppliersPage'),
  '/pharmacy/reorder': () => import('../components/pharmacy/purchases/ReorderListPage'),
  '/pharmacy/reports': () => import('../components/pharmacy/reports/PharmacyReportsPage'),
  '/profile': () => import('../components/profile/ProfileScreen'),
  '/referrals': () => import('../pages/referrals/ReferralDashboardPage'),
  '/reports': () => import('../components/reports/DailyReport'),
  '/settings/departments': () => import('../components/settings/DepartmentsSettings'),
  '/settings/doctors': () => import('../components/settings/DoctorsSettings'),
  '/settings/forms': () => import('../components/settings/forms/FormsLibraryPage'),
  '/settings/specialty-packs': () => import('../components/settings/SpecialtyPacksSettings'),
  '/settings/specialty-features': () => import('../components/settings/SpecialtyFeaturesSettings'),
  '/settings/consent-forms': () => import('../components/consentForms/ConsentLibraryPage'),
  '/settings/recall-rules': () => import('../components/recalls/RecallRulesPage'),
  '/transfers': () => import('../pages/transfers/TransferDashboardPage'),
};

/** Pages whose component depends on who is signed in (App.tsx RoleDashboard / TokensRouteGate). */
export const ROLE_ROUTE_LOADERS: Record<string, (role: string | undefined) => RouteLoader[]> = {
  '/dashboard': (role) => {
    switch (role) {
      case 'doctor': return [() => import('../components/doctor/DoctorDashboard')];
      case 'lab_admin': case 'lab_staff': case 'lab_assistant': return [() => import('../components/lab/workflow/LabWorklistPage')];
      case 'pharmacy_admin': case 'pharmacy_staff': return [() => import('../components/pharmacy/PharmacyDashboard')];
      case 'super_admin': return [() => import('../components/admin/SuperAdminDashboard')];
      default: return [() => import('../components/admin/ClinicDashboard')];
    }
  },
  '/tokens': (role) => [
    () => import('../components/appointments/AppointmentsHub'),
    role === 'clinic_admin' || role === 'staff'
      ? () => import('../pages/appointments/AppointmentCreatePage')
      : () => import('../components/tokens/TokenList'),
  ],
};

const started = new Set<string>();

/**
 * Starts loading the chunk(s) behind `path`, once per page load; later calls for the same path are no-ops. Errors
 * are swallowed — a failed warm-up changes nothing, the route's own lazy import will retry and report.
 */
export function preloadRoute(path: string, role?: string): void {
  const key = `${path}|${role ?? ''}`;
  if (started.has(key)) return;
  const loaders = ROLE_ROUTE_LOADERS[path]?.(role) ?? (ROUTE_LOADERS[path] ? [ROUTE_LOADERS[path]] : []);
  if (loaders.length === 0) return;
  started.add(key);
  for (const load of loaders) {
    try {
      load().catch(() => { /* the real navigation reports load failures */ });
    } catch { /* not a module environment */ }
  }
}

/** Test seam: forget what has been warmed. */
export function _resetPreloads(): void {
  started.clear();
}
